import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Tooltip,
  Typography,
  InputAdornment
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import StorageIcon from '@mui/icons-material/StorageOutlined';
import VisibilityIcon from '@mui/icons-material/VisibilityOutlined';
import HelpIcon from '../help/HelpIcon';
import AccountUsersAutocomplete from '../common/AccountUsersAutocomplete';

const defaultFormValues = {
  connection_id: '',
  name: '',
  description: '',
  usersAllowed: []
};

const extractConnectionValue = (value) => {
  if (value === undefined || value === null) {
    return undefined;
  }

  if (typeof value === 'object' && !Array.isArray(value)) {
    if (Object.prototype.hasOwnProperty.call(value, 'S')) {
      return value.S;
    }
    if (Object.prototype.hasOwnProperty.call(value, 'N')) {
      return value.N;
    }
    if (Object.prototype.hasOwnProperty.call(value, 'BOOL')) {
      return value.BOOL;
    }
  }

  return value;
};

const resolveConnectionField = (connection, candidates = []) => {
  for (const key of candidates) {
    const rawValue = extractConnectionValue(connection?.[key]);
    if (rawValue !== undefined && rawValue !== null) {
      if (typeof rawValue === 'string') {
        const trimmed = rawValue.trim();
        if (trimmed) {
          return trimmed;
        }
      } else {
        return rawValue;
      }
    }
  }

  return '';
};

const connectionIdKeys = [
  'connection_id',
  'connectionId',
  'id',
  'ID',
  'source_connection_id',
  'sourceConnectionId'
];

const connectionTitleKeys = [
  'database_name',
  'databaseName',
  'connection_name',
  'connectionName',
  'name'
];

const connectionSubtitleKeys = ['connection_type', 'connectionType', 'type'];

const resolveConnectionId = (connection, index) => {
  const resolved = resolveConnectionField(connection, connectionIdKeys);
  if (resolved === '' || resolved === undefined || resolved === null) {
    return `connection-${index}`;
  }
  return String(resolved);
};

const resolveConnectionTitle = (connection) => {
  const resolved = resolveConnectionField(connection, connectionTitleKeys);
  if (resolved === '' || resolved === undefined || resolved === null) {
    return 'Sin nombre';
  }
  return String(resolved);
};

const resolveConnectionSubtitle = (connection) => {
  const resolved = resolveConnectionField(connection, connectionSubtitleKeys);
  if (resolved === '' || resolved === undefined || resolved === null) {
    return '';
  }
  return String(resolved);
};

const SemanticDatabaseFormDialog = ({
  open,
  mode = 'create',
  initialValues,
  connections = [],
  onClose,
  onSubmit,
  isSaving,
  onRefreshConnections,
  isRefreshingConnections = false
}) => {
  const [formValues, setFormValues] = useState(defaultFormValues);
  const [errors, setErrors] = useState({});
  const [showConnectionDetails, setShowConnectionDetails] = useState(false);

  const connectionsOptions = useMemo(() => connections || [], [connections]);
  const isEditMode = mode === 'edit';
  
  const selectedConnection = useMemo(() => {
    if (!formValues.connection_id) return null;
    return connectionsOptions.find((conn, index) => {
      return resolveConnectionId(conn, index) === formValues.connection_id;
    });
  }, [formValues.connection_id, connectionsOptions]);

  useEffect(() => {
    if (!open) {
      setFormValues(defaultFormValues);
      setErrors({});
      return;
    }

    if (initialValues) {
      setFormValues({
        connection_id:
          initialValues.connection_id ||
          initialValues.connectionId ||
          initialValues.id ||
          initialValues.ID ||
          '',
        name: initialValues.name || '',
        description: initialValues.description || '',
        usersAllowed: Array.isArray(initialValues.users_allowed)
          ? initialValues.users_allowed.map((item) => item?.user_id || item).filter(Boolean)
          : []
      });
    } else {
      setFormValues(defaultFormValues);
    }
    setErrors({});
  }, [open, initialValues]);

  const validate = () => {
    const currentErrors = {};

    if (!formValues.connection_id) {
      currentErrors.connection_id = 'Selecciona una conexión';
    }

    if (!formValues.name.trim()) {
      currentErrors.name = 'Ingresa un nombre';
    }

    setErrors(currentErrors);
    return Object.keys(currentErrors).length === 0;
  };

  const handleChange = (key) => (event) => {
    setFormValues((prev) => ({ ...prev, [key]: event.target.value }));
  };

  const handleRefreshConnections = async () => {
    if (onRefreshConnections) {
      await onRefreshConnections();
    }
  };

  const handleUsersChange = (usersAllowed) => {
    setFormValues((prev) => ({ ...prev, usersAllowed }));
  };

  const handleSubmit = () => {
    if (!validate()) {
      return;
    }

    const payload = {
      connection_id: formValues.connection_id,
      name: formValues.name.trim(),
      description: formValues.description.trim(),
      users_allowed: formValues.usersAllowed.map((userId) => ({ user_id: userId }))
    };

    onSubmit?.(payload);
  };

  return (
    <>
    <Dialog
      open={open}
      onClose={isSaving ? undefined : onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          bgcolor: 'var(--wb-bg-panel)',
          color: 'var(--wb-text-strong)',
          borderRadius: 2
        }
      }}
    >
      <DialogTitle sx={{ borderBottom: '1px solid var(--wb-overlay-10)' }}>
        {isEditMode ? 'Editar base semántica' : 'Nueva base semántica'}
      </DialogTitle>
      {/* '&&' gana a la regla de MUI que quita el padding superior tras un
          DialogTitle; sin ese espacio la etiqueta de "Conexión" queda cortada. */}
      <DialogContent sx={{ '&&': { pt: 3 } }}>
        <Stack spacing={3}>
          <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1 }}>
            <FormControl fullWidth error={Boolean(errors.connection_id)} sx={{ flexGrow: 1 }}>
              <InputLabel id="semantic-database-connection-label" shrink sx={{ color: 'var(--wb-text-label)' }}>
                Conexión
              </InputLabel>
              <Select
                labelId="semantic-database-connection-label"
                value={formValues.connection_id}
                onChange={handleChange('connection_id')}
                label="Conexión"
                notched
                displayEmpty
                renderValue={(selectedValue) => {
                  if (!selectedValue) {
                    return (
                      <Typography sx={{ color: 'var(--wb-x-text-64748b, #64748b)' }}>
                        Selecciona una conexión
                      </Typography>
                    );
                  }

                  const connectionMatch = connectionsOptions.find((connection, index) => {
                    return resolveConnectionId(connection, index) === selectedValue;
                  });

                  if (!connectionMatch) {
                    return (
                      <Typography sx={{ color: 'var(--wb-text)', fontWeight: 600 }}>
                        {selectedValue}
                      </Typography>
                    );
                  }

                  const title = resolveConnectionTitle(connectionMatch);
                  const subtitle = resolveConnectionSubtitle(connectionMatch);
                  const connectionType = connectionMatch?.connection_type || '';

                  return (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: 40,
                          height: 40,
                          borderRadius: 1,
                          bgcolor: 'var(--wb-x-bg-38bdf826, rgba(56, 189, 248, 0.15))',
                          border: '1px solid var(--wb-accent-30)'
                        }}
                      >
                        <StorageIcon sx={{ color: 'var(--wb-accent)', fontSize: 20 }} />
                      </Box>
                      <Box sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
                        <Typography sx={{ color: 'var(--wb-text)', fontWeight: 600, fontSize: 14 }}>
                          {title}
                        </Typography>
                        {subtitle && (
                          <Typography sx={{ color: 'var(--wb-muted)', fontSize: 12 }}>
                            {subtitle}
                          </Typography>
                        )}
                        {connectionType && (
                          <Box
                            sx={{
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 0.5,
                              mt: 0.5,
                              px: 1,
                              py: 0.25,
                              borderRadius: 0.5,
                              bgcolor: 'var(--wb-x-bg-94a3b826, rgba(148, 163, 184, 0.15))',
                              width: 'fit-content'
                            }}
                          >
                            <Typography sx={{ color: 'var(--wb-text-label)', fontSize: 11, fontWeight: 500, textTransform: 'uppercase' }}>
                              {connectionType}
                            </Typography>
                          </Box>
                        )}
                      </Box>
                    </Box>
                  );
                }}
                sx={{ color: 'var(--wb-text)' }}
              >
                {connectionsOptions.length === 0 && (
                  <MenuItem value="" disabled>
                    No hay conexiones disponibles
                  </MenuItem>
                )}
                {connectionsOptions.map((connection, index) => {
                  const optionValue = resolveConnectionId(connection, index);
                  const optionTitle = resolveConnectionTitle(connection);
                  const optionSubtitle = resolveConnectionSubtitle(connection);
                  const isSelected = formValues.connection_id === optionValue;

                  return (
                    <MenuItem
                      key={`${optionValue}-${index}`}
                      value={optionValue}
                      sx={{
                        alignItems: 'stretch',
                        py: 1,
                        '&.Mui-selected': { backgroundColor: 'transparent' },
                        '&.Mui-selected:hover': { backgroundColor: 'var(--wb-x-bg-94a3b814, rgba(148, 163, 184, 0.08))' },
                        '&:hover .connection-option-card': {
                          borderColor: 'var(--wb-accent)',
                          bgcolor: 'var(--wb-x-bg-38bdf814, rgba(56, 189, 248, 0.08))'
                        }
                      }}
                    >
                      <Box
                        className="connection-option-card"
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          gap: 0.5,
                          width: '100%',
                          borderRadius: 1,
                          border: '1px solid',
                          borderColor: isSelected ? 'var(--wb-accent)' : 'var(--wb-border-20)',
                          bgcolor: isSelected ? 'var(--wb-x-bg-38bdf81f, rgba(56, 189, 248, 0.12))' : 'var(--wb-bg-overlay-70)',
                          p: 1.5,
                          transition: 'background-color 0.2s ease, border-color 0.2s ease'
                        }}
                      >
                        <Typography sx={{ color: 'var(--wb-text)', fontWeight: 600 }}>
                          {optionTitle}
                        </Typography>
                        {optionSubtitle ? (
                          <Typography sx={{ color: 'var(--wb-muted)', fontSize: 12 }}>
                            {optionSubtitle}
                          </Typography>
                        ) : null}
                      </Box>
                    </MenuItem>
                  );
                })}
              </Select>
              {errors.connection_id && (
                <FormHelperText>{errors.connection_id}</FormHelperText>
              )}
            </FormControl>
            <HelpIcon module="modelo-semantico" topic="base-de-datos-semantica" anchor="conexión" />
            <Tooltip title="Ver detalles de la conexión">
              <span>
                <IconButton
                  onClick={() => setShowConnectionDetails(true)}
                  disabled={!formValues.connection_id}
                  sx={{ color: 'var(--wb-accent)' }}
                >
                  <VisibilityIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Actualizar conexiones">
              <span>
                <IconButton
                  onClick={handleRefreshConnections}
                  disabled={isRefreshingConnections || !onRefreshConnections}
                  sx={{ color: 'var(--wb-accent)' }}
                >
                  {isRefreshingConnections ? (
                    <CircularProgress size={20} sx={{ color: 'var(--wb-accent)' }} />
                  ) : (
                    <RefreshIcon fontSize="small" />
                  )}
                </IconButton>
              </span>
            </Tooltip>
          </Box>

          <TextField
            label="Nombre"
            value={formValues.name}
            onChange={handleChange('name')}
            fullWidth
            error={Boolean(errors.name)}
            helperText={errors.name}
            InputLabelProps={{ sx: { color: 'var(--wb-text-label)' } }}
            InputProps={{
              sx: { color: 'var(--wb-text)' },
              endAdornment: (
                <InputAdornment position="end">
                  <HelpIcon module="modelo-semantico" topic="base-de-datos-semantica" anchor="nombre" />
                </InputAdornment>
              )
            }}
          />

          <TextField
            label="Descripción"
            value={formValues.description}
            onChange={handleChange('description')}
            fullWidth
            multiline
            minRows={3}
            InputLabelProps={{ sx: { color: 'var(--wb-text-label)' } }}
            InputProps={{
              sx: { color: 'var(--wb-text)' },
              endAdornment: (
                <InputAdornment position="end">
                  <HelpIcon module="modelo-semantico" topic="base-de-datos-semantica" anchor="descripción" />
                </InputAdornment>
              )
            }}
          />

          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
              <Typography variant="subtitle2" sx={{ color: 'var(--wb-text-label)' }}>
                Usuarios con acceso
              </Typography>
              <HelpIcon module="modelo-semantico" topic="base-de-datos-semantica" anchor="usuarios-con-acceso" />
            </Box>
            <AccountUsersAutocomplete
              enabled={open}
              value={formValues.usersAllowed}
              onChange={handleUsersChange}
            />
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ borderTop: '1px solid var(--wb-overlay-10)', p: 2 }}>
        <Button onClick={onClose} disabled={isSaving} sx={{ color: 'var(--wb-text-label)' }}>
          Cancelar
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={isSaving}
          sx={{ bgcolor: 'var(--wb-accent)', '&:hover': { bgcolor: 'var(--wb-sky)' } }}
        >
          {isSaving ? 'Guardando...' : isEditMode ? 'Guardar cambios' : 'Crear'}
        </Button>
      </DialogActions>
    </Dialog>
    
    <Dialog
      open={showConnectionDetails}
      onClose={() => setShowConnectionDetails(false)}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: 'var(--wb-bg-panel)',
          color: 'var(--wb-text-strong)',
          borderRadius: 2
        }
      }}
    >
      <DialogTitle sx={{ borderBottom: '1px solid var(--wb-overlay-10)', display: 'flex', alignItems: 'center', gap: 1 }}>
        <StorageIcon sx={{ color: 'var(--wb-accent)' }} />
        Detalles de la conexión
      </DialogTitle>
      <DialogContent sx={{ pt: 3 }}>
        {selectedConnection ? (
          <Stack spacing={2.5}>
            <Box>
              <Typography variant="subtitle2" color="var(--wb-text-label)" sx={{ mb: 0.5 }}>
                Nombre
              </Typography>
              <Typography variant="body2" color="var(--wb-x-text-ffffffe6, rgba(255, 255, 255, 0.9))">
                {selectedConnection.connection_name || selectedConnection.enterprise_application_name || 'Sin nombre'}
              </Typography>
            </Box>
            
            <Box>
              <Typography variant="subtitle2" color="var(--wb-text-label)" sx={{ mb: 0.5 }}>
                Tipo de base de datos
              </Typography>
              <Chip
                label={selectedConnection.connection_type?.toUpperCase() || 'N/A'}
                size="small"
                sx={{
                  bgcolor: 'var(--wb-x-bg-38bdf826, rgba(56, 189, 248, 0.15))',
                  color: 'var(--wb-accent)',
                  border: '1px solid var(--wb-accent-30)',
                  fontWeight: 600
                }}
              />
            </Box>
            
            {selectedConnection.database_name && (
              <Box>
                <Typography variant="subtitle2" color="var(--wb-text-label)" sx={{ mb: 0.5 }}>
                  Base de datos
                </Typography>
                <Typography variant="body2" color="var(--wb-x-text-ffffffe6, rgba(255, 255, 255, 0.9))">
                  {selectedConnection.database_name}
                </Typography>
              </Box>
            )}
            
            {selectedConnection.description && (
              <Box>
                <Typography variant="subtitle2" color="var(--wb-text-label)" sx={{ mb: 0.5 }}>
                  Descripción
                </Typography>
                <Typography variant="body2" color="var(--wb-text-soft)">
                  {selectedConnection.description}
                </Typography>
              </Box>
            )}
            
            <Box>
              <Typography variant="subtitle2" color="var(--wb-text-label)" sx={{ mb: 0.5 }}>
                Estado
              </Typography>
              <Chip
                label={selectedConnection.connection_status === 'active' ? 'Activa' : selectedConnection.connection_status || 'Desconocido'}
                size="small"
                sx={{
                  bgcolor: selectedConnection.connection_status === 'active' ? 'var(--wb-x-bg-22c55e26, rgba(34, 197, 94, 0.15))' : 'var(--wb-x-bg-94a3b826, rgba(148, 163, 184, 0.15))',
                  color: selectedConnection.connection_status === 'active' ? 'var(--wb-x-text-22c55e, #22c55e)' : 'var(--wb-muted)',
                  border: `1px solid ${selectedConnection.connection_status === 'active' ? 'var(--wb-x-border-22c55e4c, rgba(34, 197, 94, 0.3))' : 'var(--wb-border-30)'}`,
                  fontWeight: 600
                }}
              />
            </Box>
            
            {selectedConnection.is_public !== undefined && (
              <Box>
                <Typography variant="subtitle2" color="var(--wb-text-label)" sx={{ mb: 0.5 }}>
                  Visibilidad
                </Typography>
                <Chip
                  label={selectedConnection.is_public ? 'Pública' : 'Privada'}
                  size="small"
                  sx={{
                    bgcolor: selectedConnection.is_public ? 'var(--wb-x-bg-fbbf2426, rgba(251, 191, 36, 0.15))' : 'var(--wb-x-bg-94a3b826, rgba(148, 163, 184, 0.15))',
                    color: selectedConnection.is_public ? 'var(--wb-warning)' : 'var(--wb-muted)',
                    border: `1px solid ${selectedConnection.is_public ? 'var(--wb-x-border-fbbf244c, rgba(251, 191, 36, 0.3))' : 'var(--wb-border-30)'}`,
                    fontWeight: 600
                  }}
                />
              </Box>
            )}
          </Stack>
        ) : (
          <Typography color="var(--wb-text-soft)">
            No hay conexión seleccionada
          </Typography>
        )}
      </DialogContent>
      <DialogActions sx={{ borderTop: '1px solid var(--wb-overlay-10)', p: 2 }}>
        <Button onClick={() => setShowConnectionDetails(false)} sx={{ color: 'var(--wb-text-label)' }}>
          Cerrar
        </Button>
      </DialogActions>
    </Dialog>
    </>
  );
};

export default SemanticDatabaseFormDialog;
